import { get } from '../../helpers/api';

/**
 * Compatibility shim.
 *
 * `GET /admin/cities` returns Postgres-shaped `AdminCityDto`s, but eleven
 * downstream consumers were written against the old GraphQL `cities` query
 * shape and still read that shape (URL params, a driver's `homeCity`, the
 * backend's `@IsMongoId() cityMongoId` field, and two map-panning call sites
 * that destructure `cityCoord.coordinates`). This function is the single
 * place that reconciles the two shapes so those call sites stay untouched.
 *
 * Remove this mapping once those consumers are modernised to read the REST
 * field names (`mongoId`, `name`, `state`, `isOperational`, `latitude`/
 * `longitude`) directly.
 *
 * `id` is deliberately `city.mongoId`, NOT REST's own `id`: REST's `id` is
 * the Postgres uuid, while every consumer treats `city.id` as the 24-hex
 * Mongo id. Passing the uuid through would break URL-param lookups silently
 * and corrupt a driver's `homeCity` write destructively.
 *
 * `cityCoord.coordinates` is synthesized as `[longitude, latitude]` to match
 * GeoJSON order — two consumers destructure `const [lng, lat] = coordinates`
 * and pan a map to it; reversed, the map jumps to the wrong hemisphere.
 */
const toCompatShape = (city) => ({
  id: city.mongoId,
  cityName: city.name,
  stateAbbr: city.state,
  operational: city.isOperational,
  timeZone: city.timeZone,
  cityCoord: { coordinates: [city.longitude, city.latitude] },
});

/**
 * All cities, optionally filtered to operational (or non-operational) ones.
 *
 * `operational` is sent as the string `'true'`/`'false'`, not a numeric 1/0:
 * the backend deliberately parses it without `@Type(() => Boolean)` (that
 * would coerce `"false"` to `true`), so `?operational=1` is a known footgun
 * that silently means false server-side.
 */
const getCities = async ({ operational } = {}) => {
  if (operational === undefined) {
    const { data } = await get('/admin/cities');
    return data.map(toCompatShape);
  }
  if (typeof operational !== 'boolean') {
    throw new Error(`Invalid operational filter: expected a boolean, got ${typeof operational}`);
  }
  const params = new URLSearchParams({ operational: operational ? 'true' : 'false' });
  const { data } = await get(`/admin/cities?${params.toString()}`);
  return data.map(toCompatShape);
};

export default {
  getCities,
};
